import type { DimensionUnit, CapacityUnit } from '@/components/specs/types'

export type FormatStyle = 'compact' | 'display' | 'title' | 'filter'

export interface FormatDimensionOptions {
  style?: FormatStyle
  includeUnit?: boolean
  capacityUnit?: CapacityUnit
  dimensionUnit?: DimensionUnit
}

export interface Dimensions {
  length?: number
  width?: number
  height?: number
  capacity?: number
  unit?: DimensionUnit
  capacityUnit?: CapacityUnit
}

export function formatDimension(
  dim: Dimensions | null | undefined,
  options: FormatDimensionOptions = {}
): string | null {
  if (!dim) return null

  const {
    style = 'display',
    includeUnit = false,
    capacityUnit = dim.capacityUnit ?? 'gal',
    dimensionUnit = dim.unit ?? 'in',
  } = options

  const hasCapacity = dim.capacity !== undefined
  const hasPhysical = dim.length !== undefined && dim.width !== undefined

  if (hasCapacity && !hasPhysical) {
    return formatCapacityOnly(dim.capacity!, capacityUnit, style)
  }

  if (hasCapacity && hasPhysical) {
    const capacityStr = formatCapacityOnly(dim.capacity!, capacityUnit, style)
    const physicalStr = formatPhysicalDimensions(dim, dimensionUnit, includeUnit, style)
    return physicalStr ? `${capacityStr} (${physicalStr})` : capacityStr
  }

  if (hasPhysical) {
    return formatPhysicalDimensions(dim, dimensionUnit, includeUnit, style)
  }

  return null
}

function formatCapacityOnly(
  capacity: number,
  unit: CapacityUnit,
  style: FormatStyle
): string {
  const unitLabel = unit === 'L' ? 'L' : unit === 'lbs' ? 'lbs' : 'gal'
  if (style === 'title') {
    if (unit === 'L') return `${capacity} Liter`
    if (unit === 'lbs') return `${capacity} lbs`
    return `${capacity} Gallon`
  }
  return `${capacity} ${unitLabel}`
}

function formatPhysicalDimensions(
  dim: Dimensions,
  unit: DimensionUnit,
  includeUnit: boolean,
  style: FormatStyle
): string | null {
  if (dim.length === undefined || dim.width === undefined) {
    return null
  }

  const separator = style === 'title' ? 'x' : style === 'compact' ? '×' : ' × '
  const unitSuffix = includeUnit ? (style === 'display' ? ` ${unit}` : unit) : ''

  if (dim.height !== undefined) {
    return `${dim.length}${separator}${dim.width}${separator}${dim.height}${unitSuffix}`
  }

  return `${dim.length}${separator}${dim.width}${unitSuffix}`
}

export function formatDimensionsCompact(dimensions: Dimensions | null | undefined): string | null {
  return formatDimension(dimensions, { style: 'compact' })
}

export function getDimensionType(
  dim: Dimensions | null | undefined
): '2d' | '3d' | 'capacity' | undefined {
  if (!dim) return undefined

  const hasCapacity = dim.capacity !== undefined
  const hasHeight = dim.height !== undefined
  const has2D = dim.length !== undefined && dim.width !== undefined

  if (hasCapacity && !has2D) return 'capacity'
  if (has2D && hasHeight) return '3d'
  if (has2D) return '2d'

  return undefined
}
